/**
 * Phase 8 T-19 §22.A — Library source (network) card.
 *
 * Each connected network renders as a card with:
 *   icon · name · connection count · sync status chip · uploader attribution
 *
 * The add-placeholder card uses a dashed border + zinc-50 bg.
 */

import { Users, RefreshCw, AlertCircle, CheckCircle2, Plus } from 'lucide-react';
import type { LibraryNetwork, SyncStatus } from '../types';

// ── Sync status chip ─────────────────────────────────────────────────────────

function SyncChip({ status }: { status: SyncStatus }) {
  if (status === 'synced') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-400">
        <CheckCircle2 className="h-3 w-3" aria-hidden />
        Fully synced
      </span>
    );
  }
  if (status === 'syncing') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-400">
        <RefreshCw className="h-3 w-3 animate-spin" aria-hidden />
        Syncing
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-red-500/10 px-2 py-0.5 text-[11px] font-medium text-red-700 dark:text-red-400">
      <AlertCircle className="h-3 w-3" aria-hidden />
      Failed
    </span>
  );
}

// ── Network icon ─────────────────────────────────────────────────────────────

function NetworkIcon({ initials }: { initials: string }) {
  return (
    <div
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-[13px] font-semibold"
      style={{
        backgroundColor: 'color-mix(in srgb, var(--ggj-accent,#10B981) 15%, transparent)',
        color: 'var(--ggj-accent,#10B981)',
        border: '1px solid color-mix(in srgb, var(--ggj-accent,#10B981) 30%, transparent)',
      }}
      aria-hidden
    >
      {initials}
    </div>
  );
}

// ── Network card ─────────────────────────────────────────────────────────────

interface SourceCardProps {
  network: LibraryNetwork;
  selected: boolean;
  onClick: () => void;
}

export function SourceCard({ network, selected, onClick }: SourceCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'flex w-full flex-col gap-3 rounded-xl border p-4 text-left transition-shadow',
        selected
          ? 'border-[#10B981] ring-1 ring-[#10B981]/40 vscode-bg-secondary'
          : 'vscode-border vscode-bg-secondary hover:shadow-md',
      ].join(' ')}
      aria-pressed={selected}
    >
      <div className="flex items-start justify-between gap-2">
        <NetworkIcon initials={network.iconInitials} />
        <SyncChip status={network.syncStatus} />
      </div>
      <div>
        <p className="text-[13px] font-semibold vscode-text-primary">{network.name}</p>
        <p className="mt-0.5 flex items-center gap-1 text-[12px] vscode-text-tertiary">
          <Users className="h-3.5 w-3.5" aria-hidden />
          {network.connectionCount.toLocaleString()} connections
        </p>
      </div>
      <p className="text-[11px] vscode-text-tertiary">
        Uploaded by {network.uploadedBy}
      </p>
    </button>
  );
}

// ── Add-network placeholder card ─────────────────────────────────────────────

interface AddNetworkCardProps {
  onClick?: () => void;
}

export function AddNetworkCard({ onClick }: AddNetworkCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-zinc-300 bg-zinc-50 p-6 transition-colors hover:border-zinc-400 dark:border-zinc-700 dark:bg-zinc-900/40 dark:hover:border-zinc-500"
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-dashed border-zinc-300 text-zinc-400 dark:border-zinc-600">
        <Plus className="h-5 w-5" aria-hidden />
      </div>
      <div className="text-center">
        <p className="text-[13px] font-medium vscode-text-secondary">Add networks / Invite members</p>
        <p className="mt-0.5 text-[11px] vscode-text-tertiary">
          Connect a LinkedIn export or invite your team
        </p>
      </div>
    </button>
  );
}
